import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getChatNotifications,
  getStudents,
} from "../../../services/admin.services";
import { formatDateTime } from "../../../utils/dateFormat";
import { Link, useNavigate } from "react-router-dom"; // Assuming you're using React Router

function MessageTable() {
  const isTableFullScreen = useSelector(
    (state) => state.application.isTableFullScreen
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [students, setStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [allMessages, setAllMessages] = useState([]);
  const [messageCount, setMessageCount] = useState(0);
  const [viewingAll, setViewingAll] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await getStudents();
        setStudents(response.data.data);
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };

    fetchStudents();
  }, []);

  const getNotifications = async () => {
    try {
      const res = await getChatNotifications();
      const notifications = res.data.notifications || [];

      setAllMessages(notifications);
      setMessageCount(notifications.length);

      // setAllMessages(res.data.notifications);
      // setMessageCount(res.data.length);

      // If not in "view all" mode, apply pagination
      if (!viewingAll) {
        const paginatedMessages = getPaginatedMessages(
          res.data,
          currentPage,
          pageSize
        );
        setMessages(paginatedMessages);
      } else {
        setMessages(res.data);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  // Function to paginate messages
  const getPaginatedMessages = (allMsgs, page, size) => {
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    return allMsgs.slice(startIndex, endIndex);
  };

  // Handle page navigation
  const goToNextPage = () => {
    const totalPages = Math.ceil(messageCount / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNavigate = (studentId, queryOn) => {
    navigate(`/admin/application/student/${studentId}/`, {
      state: {
        queryOn,
      },
    });
  };

  // Toggle view all functionality
  const toggleViewAll = () => {
    setViewingAll(!viewingAll);
    if (!viewingAll) {
      // Show all messages
      setMessages(allMessages);
    } else {
      // Go back to paginated view
      const paginatedMessages = getPaginatedMessages(
        allMessages,
        currentPage,
        pageSize
      );
      setMessages(paginatedMessages);
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  // Update messages when pagination changes
  useEffect(() => {
    if (!viewingAll && allMessages.length > 0) {
      const paginatedMessages = getPaginatedMessages(
        allMessages,
        currentPage,
        pageSize
      );
      setMessages(paginatedMessages);
    }
  }, [currentPage, pageSize, viewingAll]);

  function extractMessage(content) {
    const parts = content.split(":");
    return parts.length > 1 ? parts.slice(1).join(":").trim() : content;
  }
  return (
    <div className="flex flex-col h-[500px]">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between shadow-lg shadow-indigo-500/20">
        <div className="flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
            <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
          </svg>
          <h2 className="font-semibold tracking-wide">Messages from Students CRM</h2>
          <span className="bg-white/20 border border-white/30 px-2 py-0.5 rounded-full text-xs font-medium">
            {allMessages.filter((item) => !item.is_read).length || 0}
          </span>
        </div>

        {/* View All button - either a Link component or a button based on requirements */}
        {allMessages.length > pageSize && (
          <button
            onClick={toggleViewAll}
            className="text-sm font-medium hover:underline flex items-center bg-white/10 hover:bg-white/20 rounded-lg px-2.5 py-1 transition-colors"
          >
            {viewingAll ? "View Less" : "View All"}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 ml-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}

        {/* Alternatively, if it should navigate to a separate page:
        <Link 
          to="/messages" 
          className="text-sm font-medium hover:underline flex items-center"
        >
          View All
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 ml-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Link>
        */}
      </div>

      {/* Table */}
      <div className="relative flex-grow overflow-auto border-x border-slate-200/80">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-600 text-sm border-b border-slate-200/80">
              <th className="p-4 font-medium">Date & Time</th>
              <th className="p-4 font-medium">Student Name</th>
              <th className="p-4 font-medium">Query on</th>
              <th className="p-4 font-medium">Message</th>
            </tr>
          </thead>
          <tbody>
            {messages.length > 0 ? (
              messages.map((msg, index) => (
                <tr
                  key={index}
                  className={`border-b border-slate-100 text-sm hover:bg-slate-50/80 transition-colors ${
                    !msg.is_read ? "bg-indigo-50/60" : ""
                  }`}
                  onClick={() => handleNavigate(msg.sender, msg.query_on)}
                >
                  <td className="p-4 text-gray-700">
                    {formatDateTime(msg.timestamp)}
                  </td>
                  <td className="p-4 text-gray-700 font-medium">
                    {students.filter(
                      (item) => item.student_id === msg.sender
                    )[0]?.username || "-"}
                  </td>
                  <td className="p-4">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs border border-slate-200">
                      {msg.query_on || "-"}
                    </span>
                  </td>
                  <td className="p-4 text-gray-600 truncate max-w-xs">
                    {extractMessage(msg.content) || "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="p-6 text-center text-gray-500">
                  No messages found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with working pagination */}
      <div className="bg-slate-50 px-4 py-3 border border-slate-200/80 rounded-b-2xl flex justify-between items-center text-sm text-slate-500">
        <span>
          {viewingAll
            ? `Showing all ${messageCount} message(s)`
            : `Showing ${Math.min(
                (currentPage - 1) * pageSize + 1,
                messageCount
              )}-${Math.min(
                currentPage * pageSize,
                messageCount
              )} of ${messageCount} message(s)`}
        </span>
        <div className="flex space-x-1">
          <button
            className={`px-3 py-1 rounded-lg border border-slate-300 ${
              currentPage > 1
                ? "hover:bg-slate-100 cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            }`}
            onClick={goToPrevPage}
            disabled={currentPage === 1 || viewingAll}
          >
            &laquo; Prev
          </button>
          <button
            className={`px-3 py-1 rounded-lg border border-slate-300 ${
              currentPage < Math.ceil(messageCount / pageSize)
                ? "hover:bg-slate-100 cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            }`}
            onClick={goToNextPage}
            disabled={
              currentPage >= Math.ceil(messageCount / pageSize) || viewingAll
            }
          >
            Next &raquo;
          </button>
        </div>
      </div>
    </div>
  );
}

export default MessageTable;
